/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react'
import {
    Button,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Select,
    MenuItem,
    Box,
    Input,
} from '@mui/material'
import { CompellingEvidenceLauncher, LoadingIndicator } from 'components'
import { useDropzone } from 'react-dropzone'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import uploadIcon from 'assets/icons/upload-icon-bg-alt.png'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import useIsMounted from 'hooks/utils/useIsMounted'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
export interface FileDnDProps {
    onDrop: (fileList: File[]) => void
    icon?: string
    iconComponent?: React.ReactNode | null
    accepts?: string[] | string
    fileUploadInstructionText?: string | React.ReactNode
    fileUploadCriteria?: string | React.ReactNode
    maxUploadFileSize?: number
    enableCustomFileDnDFeature?: boolean
    customFileDnDFeature?: React.ReactNode
    disableFileDnD?: boolean
    enableFileDnDSlimStyling?: boolean
    enableMobileUI?: boolean
    hoverTheming?: {
        labelColor?: string
        borderColor?: string
        backgroundColor?: string
    }
    className?: string
}

/**
 * Drag and drop file upload area.
 *
 * @param props {@link FileDnDProps}
 */
export const FileDnD = ({
    onDrop,
    icon = uploadIcon,
    iconComponent = null,
    accepts,
    fileUploadInstructionText,
    fileUploadCriteria,
    maxUploadFileSize = 5242880,
    enableCustomFileDnDFeature = false,
    customFileDnDFeature,
    disableFileDnD,
    enableFileDnDSlimStyling = false,
    enableMobileUI = false,
    hoverTheming = {},
    className = '',
}: FileDnDProps): JSX.Element => {
    const { clientTheme } = useThemeStyling()
    const { user } = useAuthedUser()
    const isReadOnly = user?.is_read_only

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: accepts,
        maxSize: maxUploadFileSize,
        maxFiles: 1,
        onDrop,
        disabled: isReadOnly || disableFileDnD,
    })

    return !enableCustomFileDnDFeature ? (
        enableMobileUI ? (
            <label htmlFor="csv-file-input">
                <Input
                    id="csv-file-input"
                    name="file"
                    type="file"
                    style={{ display: 'none' }}
                    inputProps={{
                        accept: accepts,
                    }}
                    onChange={(e: any) => {
                        onDrop(e.target.files)
                    }}
                />
                <Button
                    sx={clientTheme.buttons.defaultButton.style}
                    variant="contained"
                    component="span"
                    style={{ width: '100%', padding: '12px 32px' }}
                >
                    Upload Files
                </Button>
                {fileUploadCriteria && (
                    <Typography
                        variant="body1"
                        className="emp-fileDnD-fileUploadCriteriaMobile"
                    >
                        {fileUploadCriteria}
                    </Typography>
                )}
            </label>
        ) : (
            <Box
                {...(getRootProps() as any)} // TODO: figure why this type error is happening
                className={`${
                    enableFileDnDSlimStyling
                        ? 'emp-fileDnD-rootSlim'
                        : 'emp-fileDnD-root'
                } ${
                    (isReadOnly || disableFileDnD) &&
                    enableFileDnDSlimStyling &&
                    'emp-fileDnD-disabledRootSlim'
                } ${
                    (isReadOnly || disableFileDnD) &&
                    !enableFileDnDSlimStyling &&
                    'emp-fileDnD-disabledRoot'
                } ${className}`}
                style={{
                    backgroundImage: iconComponent ? 'none' : `url(${icon})`,
                }}
                sx={{
                    '&:hover': {
                        borderColor:
                            hoverTheming.borderColor ??
                            clientTheme.uploadDnD.borderColor,
                        backgroundColor:
                            hoverTheming.backgroundColor ??
                            clientTheme.uploadDnD.backgroundColor,
                        backgroundSize: enableFileDnDSlimStyling ? 75 : 240,

                        '& .MuiTypography-body1': {
                            color:
                                hoverTheming.labelColor ??
                                clientTheme.uploadDnD.labelColor,
                        },
                    },
                }}
            >
                {iconComponent && <>{iconComponent}</>}
                <input id="dz-input" {...getInputProps()} />
                <div className={'emp-fileDnD-label'}>
                    {isDragActive ? (
                        <Typography
                            className={`${
                                enableFileDnDSlimStyling &&
                                'emp-fileDnD-fileUploadInstructionTextSlim'
                            }`}
                        >
                            Drop the files here ...
                        </Typography>
                    ) : (
                        <React.Fragment>
                            <Typography
                                className={`${
                                    enableFileDnDSlimStyling &&
                                    'emp-fileDnD-fileUploadInstructionTextSlim'
                                }`}
                            >
                                {fileUploadInstructionText ? (
                                    fileUploadInstructionText
                                ) : (
                                    <>
                                        Drag & Drop here or{' '}
                                        <span
                                            style={{
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            Choose File
                                        </span>
                                    </>
                                )}
                            </Typography>
                            {fileUploadCriteria && (
                                <Typography
                                    className={
                                        enableFileDnDSlimStyling
                                            ? 'emp-fileDnD-fileUploadCriteriaSlim'
                                            : 'emp-fileDnD-fileUploadCriteria'
                                    }
                                >
                                    {fileUploadCriteria}
                                </Typography>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </Box>
        )
    ) : (
        <div className={'emp-fileDnD-customFileDnDFeatureContainer'}>
            {customFileDnDFeature}
        </div>
    )
}

interface FileUploadProps {
    /**  */
    onUpload: (fileList: File[]) => void
    /**  */
    onTemplateListSelect?: (template: any) => void
    /**  */
    onFileDragAndDrop?: (fileList: File[]) => void
    /** */
    loadingFileDragAndDrop: boolean
    /** */
    onUnstage: (val: any) => void
    /** */
    onReorderCombinedDocuments?: (val: any) => void
    /**  */
    files: File[]
    /** title to display over uploaded file list. */
    fileListTitle?: string
    /** file types to accept */
    accepts: string | string[]
    /** max upload file size */
    maxUploadFileSize: number
    /** Text to display in file upload component */
    fileUploadInstructionText?: string | React.ReactNode
    /** Is this running in Chargebacks - upload representment modal? */
    isRepresentmentModal?: boolean
    /** Will be passed if this is running in representment upload modal */
    templatesList?: any
    combinedDocuments?: any
    isMultiSelect?: boolean
    showCompellingEvidence?: boolean
    setCompellingEvidenceModalOpen?: (
        open: boolean,
        closeRepresentment?: boolean
    ) => void
}

/**
 * helper to format byte size to a human readable form.
 *
 * @param bytes - size of file in number of bytes.
 *
 * @returns {string} - human readable size string i.e 1.3KB, 1MB.
 */
function bytesToSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 Byte'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${Math.round(bytes / 1024 ** i)} ${sizes[i]}`
}

/**
 * Use to upload files in the browser.
 *
 */
export const FileUpload = ({
    files,
    fileListTitle = 'Files Added',
    onUpload,
    onUnstage,
    onTemplateListSelect,
    onFileDragAndDrop,
    loadingFileDragAndDrop,
    onReorderCombinedDocuments = () => {},
    accepts,
    maxUploadFileSize,
    fileUploadInstructionText,
    isRepresentmentModal = false,
    templatesList,
    combinedDocuments,
    isMultiSelect = false,
    showCompellingEvidence = false,
    setCompellingEvidenceModalOpen = (
        _open: boolean,
        _closeRepresentment?: boolean
    ) => {},
}: FileUploadProps): JSX.Element => {
    const { clientTheme } = useThemeStyling()
    const { client } = useFeatureToggle('CLIENT')
    const { style: formVariantStyle = 'outlined' } = useFeatureToggle(
        'FORM_FIELDS'
    )
    const [selectedTemplateId, setSelectedTemplateId] = useState(-1)
    const [documentDisplayTitles, setDocumentDisplayTitles] = useState([])

    const { isMounted } = useIsMounted()

    // with multiple cases are selected for doc upload, each case gets the same document which results in duplicate combined files - this removes any duplicates so that the document names can be displayed correctly in the DnD below
    useEffect(() => {
        if (isMounted.current && combinedDocuments?.length) {
            // if multi select, the combined documents come in as an array of case ids with attached files, we just need the file names, so we can hard target the first index case of combinedDocuments to read the file array within to display what files have been uploaded
            isMultiSelect
                ? setDocumentDisplayTitles(combinedDocuments[0].files)
                : setDocumentDisplayTitles(combinedDocuments)
        }
    }, [combinedDocuments, isMultiSelect, isMounted])

    return (
        <Grid container>
            {/* Upload Zone */}
            <Grid item xs={12} sm={6}>
                {isRepresentmentModal && (
                    <Grid className={'emp-fileUpload-templateLibContainer'}>
                        <Select
                            labelId="template-field-label"
                            id="template-field"
                            name="template"
                            value={selectedTemplateId}
                            onChange={(e: any) => {
                                setSelectedTemplateId(e.target.value)
                            }}
                            variant={formVariantStyle}
                            sx={[
                                formVariantStyle === 'standard' && {
                                    '& .MuiInputBase-input': {
                                        ...clientTheme.formFields.formText
                                            .standard,
                                        borderBottom: '1px solid #84878E',
                                    },
                                },
                                {
                                    '& .MuiOutlinedInput-input': {
                                        padding: '7px',
                                    },
                                },
                            ]}
                        >
                            <MenuItem value={-1}>
                                Choose a template from list.
                            </MenuItem>
                            {templatesList.map((option: any) => {
                                return (
                                    <MenuItem
                                        key={`template-option-${option.value}`}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                )
                            })}
                        </Select>
                        <Button
                            size={'small'}
                            style={{
                                fontSize: clientTheme.typography.body1.fontSize,
                            }}
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                                const foundTemplateFile = templatesList.find(
                                    (template: any) =>
                                        template.value === selectedTemplateId
                                )
                                if (foundTemplateFile && onTemplateListSelect) {
                                    onTemplateListSelect(foundTemplateFile)
                                }
                            }}
                            sx={[
                                client === 'jpmc' &&
                                    clientTheme.buttons.greyButton.style,
                            ]}
                        >
                            Add
                        </Button>
                    </Grid>
                )}
                <FileDnD
                    fileUploadInstructionText={fileUploadInstructionText}
                    onDrop={onFileDragAndDrop || onUpload}
                    accepts={accepts}
                    fileUploadCriteria="(Please upload only .JPG or .PDF files)"
                    maxUploadFileSize={maxUploadFileSize}
                />
            </Grid>
            {/* File List */}
            <Grid item xs={12} sm={6} className={'emp-fileUpload-fileRoot'}>
                {showCompellingEvidence && (
                    <CompellingEvidenceLauncher
                        isRemedyPossible={true}
                        setCompellingEvidenceModalOpen={
                            setCompellingEvidenceModalOpen
                        }
                    />
                )}
                <Typography variant="h3">{fileListTitle}</Typography>
                {loadingFileDragAndDrop && (
                    <div className="emp-fileUpload-loadingContainer">
                        <LoadingIndicator text={'Uploading...'} />
                    </div>
                )}
                {!combinedDocuments ? (
                    <List>
                        {files.map((file) => (
                            <ListItem
                                key={`${file.name}-${file.lastModified}`}
                                dense
                            >
                                <ListItemIcon>
                                    <IconButton
                                        size="small"
                                        onClick={(): void => onUnstage(file)}
                                    >
                                        <Icon icon={faTimes} />
                                    </IconButton>
                                </ListItemIcon>
                                <ListItemText>{file.name}</ListItemText>
                                <ListItemText>{`(${bytesToSize(
                                    file.size
                                )})`}</ListItemText>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <DragDropContext onDragEnd={onReorderCombinedDocuments}>
                        <Droppable droppableId="droppable">
                            {(provided, _snapshot) => (
                                <List
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {documentDisplayTitles.map(
                                        (doc: any, idx: any) => (
                                            <Draggable
                                                key={`doc-${idx}`}
                                                draggableId={`doc-${idx}`}
                                                index={idx}
                                            >
                                                {(provided, _snapshot) => (
                                                    <ListItem
                                                        dense
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <ListItemIcon>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(): void =>
                                                                    onUnstage(
                                                                        idx
                                                                    )
                                                                }
                                                            >
                                                                <Icon
                                                                    icon={
                                                                        faTimes
                                                                    }
                                                                />
                                                            </IconButton>
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            {doc.label}
                                                        </ListItemText>
                                                    </ListItem>
                                                )}
                                            </Draggable>
                                        )
                                    )}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </Grid>
        </Grid>
    )
}

export default FileUpload
